import { Box, Typography } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { BORDER_RADIUS } from "app/theme";
import DetailDialog from "common/components/DetailDialog";
import { useState } from "react";
import {
  AnimalPerformanceCard,
  BiodiversityCard,
  CarbonFootprintCard,
  MilkQualityCard,
  WaterSoilCard,
} from "../AssessmentCards";
import {
  useFarmerActions,
  useProgrammeInfo,
} from "../hooks/useInsightsDashboardQueries";
import type { InsightCategory } from "../InsightDetailDialog";
import InsightDetailDialog from "../InsightDetailDialog";
import FarmerFilesTable from "./FarmerFilesTable";

// ─── Actions Table ──────────────────────────────────────────────

function ActionsTable({ farmerGuid }: { farmerGuid: string }) {
  const { data, isLoading } = useFarmerActions(farmerGuid);
  const actions = data?.data ?? [];

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: BORDER_RADIUS.small + "px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        mb: 3,
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b" }}
        >
          Submitted Actions
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr auto",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          Category
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          Action
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
        >
          Submitted
        </Typography>
      </Box>

      {isLoading ? (
        [1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr auto",
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #f4f4f5",
            }}
          >
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width={160} height={18} />
            <Skeleton
              variant="text"
              width={70}
              height={18}
              sx={{ ml: "auto" }}
            />
          </Box>
        ))
      ) : actions.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No actions submitted yet
          </Typography>
        </Box>
      ) : (
        actions.map((action, idx) => (
          <Box
            key={idx}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr auto",
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #f4f4f5",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#71717a", fontSize: "0.8rem" }}
            >
              {action["SubmittedActions.category"]}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#18181b", fontWeight: 500 }}
            >
              {action["SubmittedActions.action"]}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#71717a", textAlign: "right", fontSize: "0.8rem" }}
            >
              {action["SubmittedActions.submissionDate"]
                ? new Date(
                    action["SubmittedActions.submissionDate"]
                  ).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "-"}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
}

// ─── Main farmer detail dialog ──────────────────────────────────

export default function FarmerDetailDialog({
  open,
  farmerGuid,
  farmerName,
  onClose,
}: {
  open: boolean;
  farmerGuid: string | null;
  farmerName: string | null;
  onClose: () => void;
}) {
  const [detailCategory, setDetailCategory] = useState<InsightCategory | null>(
    null
  );
  const { data: programmeInfo } = useProgrammeInfo();
  const programmeType =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeType"] ?? "BEEF";
  const isDairy = programmeType === "DAIRY";

  if (!farmerGuid) return null;

  return (
    <>
      <DetailDialog
        open={open}
        onClose={onClose}
        title={farmerName ?? "Farmer Details"}
        contentSx={{ backgroundColor: "#F7FCF1" }}
      >
        <Box
          sx={{
            maxWidth: 800,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 3,
          }}
        >
          <CarbonFootprintCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("carbon-footprint")}
          />
          <BiodiversityCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("biodiversity")}
          />
          <WaterSoilCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("water-soil")}
          />
          {isDairy ? (
            <MilkQualityCard
              farmerGuid={farmerGuid}
              onClick={() => setDetailCategory("milk-quality")}
            />
          ) : (
            <AnimalPerformanceCard
              farmerGuid={farmerGuid}
              onClick={() => setDetailCategory("animal-performance")}
            />
          )}
        </Box>

        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          <ActionsTable farmerGuid={farmerGuid} />
          <FarmerFilesTable farmerGuid={farmerGuid} />
        </Box>
      </DetailDialog>

      <InsightDetailDialog
        open={detailCategory !== null}
        category={detailCategory}
        onClose={() => setDetailCategory(null)}
        farmerGuid={farmerGuid}
      />
    </>
  );
}
